import axios from 'axios';
import * as Sentry from '@sentry/react';

export type CreatePostKind = 'lead' | 'meetup' | 'thank-you' | 'testimonial';

interface CreatePostFailureOptions {
  kind: CreatePostKind;
  error: unknown;
  userId?: string | null;
  communityId?: string | null;
  request: Record<string, unknown>;
  fallbackMessage: string;
}

export function reportCreatePostFailure({
  kind,
  error,
  userId,
  communityId,
  request,
  fallbackMessage,
}: CreatePostFailureOptions): string {
  const axiosError = axios.isAxiosError(error) ? error : null;
  const responseData = axiosError?.response?.data as
    | { error?: string; field?: string; code?: string }
    | undefined;
  const status = axiosError?.response?.status;
  const serverMessage = typeof responseData?.error === 'string' ? responseData.error : undefined;

  console.error(`[CreatePost:${kind}] submission failed`, {
    kind,
    userId: userId ?? null,
    communityId: communityId ?? null,
    status: status ?? null,
    serverMessage: serverMessage ?? null,
    field: responseData?.field ?? null,
    code: responseData?.code ?? null,
    request,
  }, error);

  Sentry.withScope((scope) => {
    scope.setTag('feature', `${kind}-form`);
    scope.setTag('action', 'create');
    if (status) scope.setTag('http_status', String(status));
    if (responseData?.code) scope.setTag('error_code', responseData.code);
    if (responseData?.field) scope.setTag('error_field', responseData.field);
    if (userId) scope.setUser({ id: userId });
    scope.setContext('create_post_request', {
      kind,
      communityId: communityId ?? null,
      ...request,
    });
    scope.setContext('create_post_response', {
      status: status ?? null,
      error: serverMessage ?? null,
      field: responseData?.field ?? null,
      code: responseData?.code ?? null,
      data: responseData ?? null,
    });
    Sentry.captureException(error);
  });

  if (serverMessage) {
    return `${fallbackMessage} (${serverMessage})`;
  }
  if (status) {
    return `${fallbackMessage} (HTTP ${status})`;
  }
  return fallbackMessage;
}
